import { useCallback, useMemo, useState } from "react";

// #926 — Auto Scroll (#691) and the all-days toggle (#892) used to be single
// booleans shared across the whole session. Now that a pill row lets the
// announcer free-switch rings (no PIN), each ring needs to remember its OWN
// preferences — Ring A can be today+auto-scroll while Ring B is all-days+
// no-scroll. Both settings live in one map, keyed by ring id, under a single
// storage key (JSON, not the old two flat booleans — those legacy keys are
// simply abandoned; nothing reads them anymore).
export const RING_SETTINGS_STORAGE_KEY = "announcer-fe.ringSettings";

// Defaults for a ring that has never been switched to before — matches the
// pre-#926 defaults (auto-scroll ON, today-only) so a first-time ring doesn't
// surprise anyone.
export const DEFAULT_RING_SETTINGS = { autoScrollEnabled: true, showAllDays: false };

export function loadRingSettingsMap() {
  try {
    const raw = localStorage.getItem(RING_SETTINGS_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

export function persistRingSettingsMap(map) {
  try {
    localStorage.setItem(RING_SETTINGS_STORAGE_KEY, JSON.stringify(map));
  } catch {
    // Storage may be unavailable (private browsing, etc.) — non-fatal.
  }
}

export function settingsForRing(map, ring) {
  const stored = ring ? map[ring] : null;
  return { ...DEFAULT_RING_SETTINGS, ...(stored || {}) };
}

// #926 — per-ring settings map (auto-scroll, all-days), keyed by ring id.
// `currentRingSettings` is whichever ring is active RIGHT NOW; switching
// rings via the pill row recomputes it automatically (no extra effect
// needed) since it's derived straight from `selectedRing`.
export function useRingSettings(selectedRing) {
  const [ringSettingsMap, setRingSettingsMap] = useState(loadRingSettingsMap);
  const currentRingSettings = useMemo(
    () => settingsForRing(ringSettingsMap, selectedRing),
    [ringSettingsMap, selectedRing],
  );
  // #1857 — `selectedRing` starts `null` and resolves asynchronously
  // (AnnouncerContext's own, separate GET /class fetch -> resolveDefaultRing,
  // #1740). Before it resolves, `settingsForRing`'s `ring ? map[ring] : null`
  // makes ANY write here a no-op-in-spirit: it would key onto the string
  // "null" (computed property coercion) and get silently abandoned the
  // instant the real ring id lands, reading back as DEFAULT_RING_SETTINGS
  // regardless of what the operator just chose. Guarding the write (and
  // disabling the switches below while unresolved, so there's nothing TO
  // write yet) closes that window instead of leaving a setting that can be
  // set and then silently lost.
  const updateRingSetting = useCallback(
    (key, value) => {
      if (!selectedRing) return;
      setRingSettingsMap((prev) => {
        const next = {
          ...prev,
          [selectedRing]: { ...settingsForRing(prev, selectedRing), [key]: value },
        };
        persistRingSettingsMap(next);
        return next;
      });
    },
    [selectedRing],
  );

  return { currentRingSettings, updateRingSetting };
}
